import React from 'react';
import { Layout, Menu, Button, Drawer, Avatar, Dropdown, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { MenuOutlined, CloseOutlined, UserOutlined, SettingOutlined, TrophyOutlined, WarningFilled } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { tokens } from '../theme/tokens';
import { useSocialContext } from '../context/social';
import { useProfileCompleteness } from '../hooks/use-profile-completeness';
import { useAdmin } from '../context/admin';
import { MenuSections } from '../types/menu-sections';
import { ONLY_TIKTOK_DEMO, HIDE_MIS_PREMIOS } from '../config/demo';
import { DrupiLogo } from './drupi-logo';
import { goFullReload } from '../utils/nav';

const { Header } = Layout;

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface AppHeaderProps {
  onConnectClick?: () => void;
}

const BASE_NAV: MenuProps['items'] = [
  { key: 'home', label: 'Videos' },
  { key: 'ranking', label: 'Ranking' },
];

const pathToKey = (pathname: string): MenuSections => {
  if (pathname.startsWith('/connections')) return 'connections';
  if (pathname.startsWith('/mis-publicaciones')) return 'mis-publicaciones';
  if (pathname.startsWith('/mis-premios')) return 'mis-premios';
  if (pathname.startsWith('/next-step')) return 'next-step';
  if (pathname.startsWith('/ranking')) return 'ranking';
  if (pathname.startsWith('/stats')) return 'stats';
  if (pathname.startsWith('/settings')) return 'settings' as any;
  if (pathname.startsWith('/admin')) return 'admin';
  return 'home';
};

function useIsMobile() {
  const [mobile, setMobile] = React.useState(() => window.innerWidth < 640);
  React.useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return mobile;
}

export const AppHeader: React.FC<AppHeaderProps> = () => {
  const { accounts, logout, isAuthenticated } = useSocialContext();
  // Perfil completo = datos de pago + permisos de TikTok (unificado).
  const { loading: profileLoading, complete: profileComplete, permissionsComplete } = useProfileCompleteness();
  const { isAdmin } = useAdmin();
  // "Vista de admin" se movió del nav del header al menú de perfil (dropdown) y
  // al drawer mobile, para que llame menos la atención. Sólo aparece para admins
  // (UX: el acceso real lo gatea require_admin en el server).
  const navItems: MenuProps['items'] = BASE_NAV;
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const isMobile = useIsMobile();

  const activeKey = pathToKey(location.pathname);

  const go = (path: string) => {
    setDrawerOpen(false);
    goFullReload(path);
  };

  const handleNav = (key: string) => {
    if (key === 'home') go('/');
    else if (key === 'connections') go('/connections');
    else if (key === 'mis-publicaciones') go('/mis-publicaciones');
    else if (key === 'mis-premios') go('/mis-premios');
    else if (key === 'ranking') go('/ranking');
    else if (key === 'settings') go('/settings');
    else if (key === 'admin') go('/admin');
  };

  // Use first connected account for avatar info
  const firstAccount = accounts[0];
  const displayName = firstAccount?.username || 'Usuario';

  const followerCount   = firstAccount?.followerCount;
  const tiktokHandle    = firstAccount?.tiktokHandle;       // user.info.profile — real @handle
  const bioDescription  = firstAccount?.bioDescription;     // user.info.profile — bio
  const isVerified      = firstAccount?.isVerified === true;// user.info.profile — verified badge
  const formattedFollowers =
    typeof followerCount === 'number'
      ? followerCount.toLocaleString('es-AR')
      : null;

  // Prefer the real @handle (from user.info.profile) when available; fall back to display_name.
  // Identidad a mostrar: el @handle real (user.info.profile) SOLO si existe.
  // Si no hay handle, mostramos el nombre PELADO — sin '@', porque el display_name
  // NO es un handle (y jamás el open_id/social_id). El handle solo llega si el usuario
  // otorgó user.info.profile en el OAuth; si no lo dio, no hay handle que mostrar.
  const identityLine = tiktokHandle ? `@${tiktokHandle}` : displayName;

  const profileMenuItems: MenuProps['items'] = [
    {
      key: 'username',
      label: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '4px 0', maxWidth: 240 }}>
          <span style={{ fontWeight: 600, color: tokens.colors.primary.neutral[700], display: 'flex', alignItems: 'center', gap: 6 }}>
            {identityLine}
            {isVerified && (
              <span
                title='Verified on TikTok'
                aria-label='Verified'
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 14, height: 14, borderRadius: '50%',
                  background: '#20c0ee', color: 'white', fontSize: 10, fontWeight: 800, lineHeight: 1,
                }}
              >
                ✓
              </span>
            )}
          </span>
          {formattedFollowers !== null && (
            <span style={{ fontSize: 12, color: tokens.colors.primary.neutral[500] }}>
              {formattedFollowers} {followerCount === 1 ? 'seguidor' : 'seguidores'}
            </span>
          )}
          {bioDescription && (
            <span
              style={{
                fontSize: 11, color: tokens.colors.primary.neutral[500],
                lineHeight: 1.4, marginTop: 2,
                whiteSpace: 'normal', wordBreak: 'break-word',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}
              title={bioDescription}
            >
              {bioDescription}
            </span>
          )}
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' },
    ...(!ONLY_TIKTOK_DEMO ? [{
      key: 'connections',
      label: 'Mis redes',
      onClick: () => go('/connections'),
    }] : []),
    {
      key: 'mis-publicaciones',
      label: 'Mis publicaciones',
      onClick: () => go('/mis-publicaciones'),
    },
    ...(!HIDE_MIS_PREMIOS ? [{
      key: 'mis-premios',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <TrophyOutlined />
          Mis Premios
        </span>
      ),
      onClick: () => go('/mis-premios'),
    }] : []),
    {
      key: 'settings',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <SettingOutlined />
          Configuración
        </span>
      ),
      onClick: () => go('/settings'),
    },
    ...(isAdmin ? [{
      key: 'admin',
      label: 'Vista de admin',
      onClick: () => go('/admin'),
    }] : []),
    { type: 'divider' },
    {
      key: 'logout',
      label: <span style={{ color: tokens.colors.supporting.red[600] }}>Cerrar sesión</span>,
      onClick: () => { logout(); go('/'); },
    },
  ];

  return (
    <>
      <Header
        style={{
          background: '#ffffff',
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 64,
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        {/* Logo — centered on mobile, left on desktop */}
        <div
          onClick={() => go('/')}
          style={isMobile ? {
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
          } : {
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexShrink: 0,
          }}
        >
          <DrupiLogo height={isMobile ? 32 : 36} />
        </div>

        {/* Nav — hidden on mobile (use drawer instead) */}
        {!isMobile && (
          <Menu
            mode='horizontal'
            selectedKeys={[activeKey]}
            onClick={({ key }) => handleNav(key)}
            items={navItems}
            className='social-header-menu'
            style={{
              flex: 1,
              justifyContent: 'center',
              borderBottom: 'none',
              background: 'transparent',
              fontSize: 14,
              fontWeight: 500
            }}
          />
        )}

        {/* Right: profile avatar + hamburger */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Warn: perfil incompleto (datos de pago y/o permisos de TikTok) → avisa sin
              ser invasivo. Click → Ajustes, donde se ve qué falta. */}
          {isAuthenticated && !profileLoading && !profileComplete && (
            <Tooltip title='Faltan datos críticos en tu perfil. Completalos en Ajustes.'>
              <WarningFilled
                onClick={() => go('/settings')}
                style={{
                  // Rojo si faltan permisos de TikTok (no se puede usar la app); amarillo si
                  // solo faltan los datos de pago.
                  color: !permissionsComplete ? '#cf1322' : '#faad14',
                  fontSize: 20, cursor: 'pointer', flexShrink: 0,
                }}
              />
            </Tooltip>
          )}
          {/* Profile dropdown — show when authenticated (even if accounts empty) */}
          {isAuthenticated && (
            <Dropdown menu={{ items: profileMenuItems }} trigger={['click']} placement='bottomRight'>
              {isMobile ? (
                // Mobile (compacto): solo el avatar, idéntico a como estaba. El nombre
                // sigue dentro del menú desplegable / drawer.
                <Avatar
                  src={firstAccount?.avatarUrl}
                  icon={<UserOutlined />}
                  size={36}
                  style={{
                    cursor: 'pointer',
                    background: tokens.colors.brand.gradient,
                    border: `2px solid ${tokens.colors.brand.blue}`,
                    flexShrink: 0,
                  }}
                >
                  {displayName.charAt(0).toUpperCase()}
                </Avatar>
              ) : (
                // Desktop (web no compacto): trigger más grande con pic + nombre.
                // El nombre es el display_name que TikTok nos devuelve en el OAuth (lo
                // traemos "gratis" en la creator/user info); el @handle va de subtítulo.
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    cursor: 'pointer',
                    padding: '4px 12px 4px 4px',
                    borderRadius: 999,
                    transition: 'background 0.2s ease',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = tokens.colors.primary.neutral[100];
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Avatar
                    src={firstAccount?.avatarUrl}
                    icon={<UserOutlined />}
                    size={40}
                    style={{
                      background: tokens.colors.brand.gradient,
                      border: `2px solid ${tokens.colors.brand.blue}`,
                      flexShrink: 0,
                    }}
                  >
                    {displayName.charAt(0).toUpperCase()}
                  </Avatar>
                  <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, maxWidth: 200 }}>
                    <span
                      title={displayName}
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: tokens.colors.primary.neutral[800],
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {displayName}
                    </span>
                    {tiktokHandle && (
                      <span
                        style={{
                          fontSize: 12,
                          color: tokens.colors.primary.neutral[500],
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        @{tiktokHandle}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </Dropdown>
          )}

          {/* Hamburger – mobile only */}
          {isMobile && (
            <Button
              type='text'
              icon={<MenuOutlined style={{ color: tokens.colors.primary.neutral[600], fontSize: 18 }} />}
              onClick={() => setDrawerOpen(true)}
            />
          )}
        </div>

        {/* Bottom gradient bar */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: tokens.colors.brand.gradient
          }}
        />
      </Header>

      {/* Mobile drawer */}
      <Drawer
        placement='right'
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={260}
        closeIcon={<CloseOutlined style={{ color: tokens.colors.primary.neutral[700] }} />}
        styles={{
          body: { padding: 0, background: '#ffffff' },
          header: { background: '#ffffff', borderBottom: '1px solid rgba(0,0,0,0.08)' }
        }}
      >
        {/* User info in drawer */}
        {accounts.length > 0 && (
          <div style={{
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            borderBottom: '1px solid rgba(0,0,0,0.06)'
          }}>
            <Avatar
              src={firstAccount?.avatarUrl}
              icon={<UserOutlined />}
              size={36}
              style={{ background: tokens.colors.brand.gradient }}
            >
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
            <span style={{ fontWeight: 600, color: tokens.colors.primary.neutral[800], fontSize: 14 }}>
              {identityLine}
            </span>
          </div>
        )}

        <Menu
          mode='vertical'
          selectedKeys={[activeKey]}
          onClick={({ key }) => handleNav(key)}
          items={[
            ...navItems,
            { type: 'divider' },
            ...(!ONLY_TIKTOK_DEMO ? [{ key: 'connections', label: 'Mis redes' }] : []),
            { key: 'mis-publicaciones', label: 'Mis publicaciones' },
            ...(!HIDE_MIS_PREMIOS ? [{
              key: 'mis-premios',
              label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <TrophyOutlined />
                  Mis Premios
                </span>
              ),
            }] : []),
            {
              key: 'settings',
              label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <SettingOutlined />
                  Configuración
                </span>
              ),
            },
            ...(isAdmin ? [{ key: 'admin', label: 'Vista de admin' }] : []),
          ]}
          style={{ background: '#ffffff', border: 'none' }}
        />

        {/* Logout in drawer */}
        {accounts.length > 0 && (
          <div style={{ padding: '8px 16px', borderTop: '1px solid rgba(0,0,0,0.06)', marginTop: 8 }}>
            <Button
              block
              danger
              type='text'
              onClick={() => { logout(); setDrawerOpen(false); }}
              style={{ textAlign: 'left', fontWeight: 500 }}
            >
              Cerrar sesión
            </Button>
          </div>
        )}
      </Drawer>
    </>
  );
};
